import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  Chip,
  Divider,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import { auth } from '../config/firebase';
import { User } from '../types';
import { useAuth } from '../contexts/AuthContext';

interface UserListProps {
  onClose: () => void;
}

interface NewUserData {
  email: string;
  password: string;
  displayName: string;
}

const UserList: React.FC<UserListProps> = ({ onClose }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [deleteUser, setDeleteUser] = useState<User | null>(null);
  const [showNewUserForm, setShowNewUserForm] = useState(false);
  const [newUserData, setNewUserData] = useState<NewUserData>({
    email: '',
    password: '',
    displayName: '',
  });
  const { updateUser, deleteUser: deleteUserAuth, signUp } = useAuth();

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const currentUser = auth.currentUser;
      if (currentUser) {
        const userList: User[] = [{
          uid: currentUser.uid,
          email: currentUser.email!,
          displayName: currentUser.displayName || '',
          role: currentUser.email === 'admin@admin.com' ? 'admin' : 'user',
          createdAt: Date.now(),
          lastLogin: Date.now()
        }];

        if (currentUser.email !== 'admin@admin.com') {
          userList.push({
            uid: 'admin',
            email: 'admin@admin.com',
            displayName: 'Administrador',
            role: 'admin',
            createdAt: Date.now(),
            lastLogin: Date.now()
          });
        }

        setUsers(userList);
      }
    } catch (err) {
      setError('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setNewUserData({
      email: '',
      password: '',
      displayName: user.displayName || '',
    });
  };

  const handleSaveEdit = async () => {
    if (!editingUser) return;

    try {
      await updateUser({
        displayName: newUserData.displayName
      });
      
      setUsers(users.map(user => 
        user.uid === editingUser.uid 
          ? { ...user, displayName: newUserData.displayName }
          : user
      ));
      
      setEditingUser(null);
      setNewUserData({
        email: '',
        password: '',
        displayName: '',
      });
    } catch (err) {
      setError('Erro ao atualizar usuário');
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteUser) return;

    try {
      await deleteUserAuth();
      setUsers(users.filter(user => user.uid !== deleteUser.uid));
      setDeleteUser(null);
      onClose();
    } catch (err) {
      setError('Erro ao excluir usuário');
    }
  };

  const handleCreateUser = async () => {
    try {
      await signUp(newUserData.email, newUserData.password, newUserData.displayName);
      setShowNewUserForm(false);
      setNewUserData({
        email: '',
        password: '',
        displayName: '',
      });
      loadUsers();
    } catch (err) {
      setError('Erro ao criar usuário. Verifique os dados e tente novamente.');
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={() => setShowNewUserForm(true)}
        >
          Criar Novo Usuário
        </Button>
      </Box>

      {showNewUserForm && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Criar Novo Usuário
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Nome"
              value={newUserData.displayName}
              onChange={(e) => setNewUserData({ ...newUserData, displayName: e.target.value })}
              fullWidth
            />
            <TextField
              label="Email"
              type="email"
              value={newUserData.email}
              onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
              fullWidth
            />
            <TextField
              label="Senha"
              type="password"
              value={newUserData.password}
              onChange={(e) => setNewUserData({ ...newUserData, password: e.target.value })}
              fullWidth
            />
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button onClick={() => setShowNewUserForm(false)}>
                Cancelar
              </Button>
              <Button 
                variant="contained" 
                onClick={handleCreateUser}
                disabled={!newUserData.email || !newUserData.password || !newUserData.displayName}
              >
                Criar
              </Button>
            </Box>
          </Box>
        </Paper>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Função</TableCell>
              <TableCell>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.uid}>
                <TableCell>
                  {editingUser?.uid === user.uid ? (
                    <TextField
                      value={newUserData.displayName}
                      onChange={(e) => setNewUserData({ ...newUserData, displayName: e.target.value })}
                      size="small"
                    />
                  ) : (
                    user.displayName
                  )}
                </TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  <Chip
                    label={user.role === 'admin' ? 'Administrador' : 'Usuário'}
                    color={user.role === 'admin' ? 'primary' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {editingUser?.uid === user.uid ? (
                    <>
                      <IconButton onClick={handleSaveEdit} color="primary">
                        <SaveIcon />
                      </IconButton>
                      <IconButton onClick={() => setEditingUser(null)}>
                        <CloseIcon />
                      </IconButton>
                    </>
                  ) : (
                    <>
                      <IconButton onClick={() => handleEditUser(user)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton onClick={() => setDeleteUser(user)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={Boolean(deleteUser)}
        onClose={() => setDeleteUser(null)}
      >
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir o usuário {deleteUser?.displayName}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteUser(null)}>Cancelar</Button>
          <Button onClick={handleDeleteUser} color="error">
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserList; 